/**
 * Name art — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_lettering`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const nameArtPack: ToilePack = {
    slug: 'name-art',
    category: 'illustration',
    name: 'Name art',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'doodle', label: 'Doodle name', subject: 'the name "Sofia" in big rounded letters, each letter filled with a different small doodle pattern, hearts, stars, stripes and flowers'},
        {key: 'nature', label: 'Nature name', subject: 'the name "Leo" with leaves, vines and a small sun woven around and through the letters'},
        {key: 'space', label: 'Space name', subject: 'the name "Aarav" in bold letters among planets, a rocket and stars, the letters lightly outlined in white'},
    ],
    styles: [
        {key: 'marker', label: 'Marker', style: 'bright marker and colored-pencil illustration with bold outlines, saturated flat color and a playful hand-drawn feel', palette: 'Rainbow brights with black outlines on a white background.', framing: 'The name is centered across the frame with a margin of empty space around it.'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolor wash letters with a fine ink outline and gentle color bleed', palette: 'Pastel pink, sky blue and soft green on a cream paper background.', framing: 'The name is centered with a wide quiet margin.'},
        {key: 'sticker', label: 'Sticker', style: 'flat vector sticker art with clean shapes and a thick white die-cut border around the whole name', palette: 'A bold flat palette of five colors on a solid navy background.', framing: 'The name is centered, the sticker shape well inside the frame.'},
    ],
    directions: ['with animals', 'dark background', 'just initials'],
    directionHint: 'try: with animals',
    seo: {
        title: 'Name Art: Doodle, Nature and Space Names',
        metaDescription: 'Nine name art pieces: a first name drawn with doodles, leaves or planets, in marker, watercolor and sticker styles.',
        h1: 'Name art: a first name as a picture, in doodle, nature or space',
        tagline: 'Nine pieces of three names, each letter spelled exactly and carrying a small drawing.',
        intro: 'Name art is a first name drawn as a picture for a bedroom door, a notebook cover or a gift. The nine pieces spell three names: Sofia with a doodle in each letter, Leo with leaves and a sun, and Aarav among planets. Each is set in marker, watercolor or flat sticker art, with every letter readable. Ask for animals, a dark background or initials only.',
        useCases: [
            'a bedroom door sign',
            'a notebook or journal cover',
            'a name gift for a birthday',
            'a sticker for a laptop or a water bottle',
        ],
        faq: [
            {q: 'Are the names spelled exactly?', a: 'Yes. Sofia, Leo and Aarav are spelled as written. A name you type is a request, and its letters are kept readable.'},
            {q: 'What are the three styles?', a: 'Marker is bright colored-pencil art with bold outlines. Watercolor is soft wash letters with a fine ink outline. Sticker is flat vector art with a thick white die-cut border.'},
            {q: 'Can I get only initials?', a: 'Yes. Ask for initials only, animals or a dark background, and the same kind of piece is drawn for it.'},
            {q: 'What are the pieces for?', a: 'A bedroom door, a notebook cover or a gift, as the sheets are meant to be used.'},
        ],
    },
}
